<template>
  <header id="header"
          :class="[headerClasses, {'scrolled-up': pageScrolledUp && !hideHeader}, {'menu-open': isMobileMenuOpen} ]">
    <div class="header-container">
      <div class="wrap-container">

        <div class="wrap-left">
          <div class="logo img-container" :class="{'menu-open': isMobileMenuOpen}">
            <router-link to="/">
              <svg-icon v-if="smallLogo" :iconURL="smallLogo" class="svg-logo"/>
            </router-link>
          </div>
          <header-menu v-if="!isMobile" :wp-menu="wpMenu"/>
        </div>

        <div class="wrap-right">

          <div class="lease-address" v-if="!isMobile">
            <font-awesome-icon :icon="mapMarker"/>
            <a class="map-lease map-link" v-if="mapLinkLeaseAddress" :href="mapLinkLeaseAddress" target="_blank"
               v-html="leaseAddress"></a>
            <span class="map-lease" v-else v-html="leaseAddress"></span>
          </div>

          <div class="wrap-permalinks" v-if="!isMobile">
            <highlight-button v-if="highlightButtons"
                              v-for="(button, index) in highlightButtons"
                              :key="index"
                              v-bind="button"
            />
          </div>

          <mobile-menu-button v-if="isMobile"
                              :menu-open="isMobileMenuOpen"
                              @toggleMenu="toggleMenu"/>

        </div>

      </div>

      <header-menu-mobile v-if="isMobile" :wp-menu="wpMenu"
                          :address="leaseAddress"
                          :link-address="mapLinkLeaseAddress"
                          :icon="mapMarker"
                          :buttons="highlightButtons"
                          @closeMenu="closeMenu"
                          :menu-open="isMobileMenuOpen"/>
    </div>


    <div class="header-search" v-if="!hideHeader">
      <div class="wrap-search">
        <div class="left img-container">
          <wp-image v-if="searchLogo" :image="searchLogo"/>
        </div>

        <div class="right" v-if="filters && filters.bedrooms && !razzEmbedShouldOpen">
          <h4>Find your apartment</h4>
          <filter-property :filters="filters"/>
        </div>
      </div>
    </div>

  </header>
</template>

<script>
import HeaderLogo from './HeaderLogo'
import HeaderMenu from './HeaderMenu'
import FilterProperty from '../components/FilterProperty'
import _throttle from 'lodash/throttle'
import HighlightButton from './HighlightButton'
import {razzMenuApi} from '@/utils/api'
import HeaderMenuMobile from './HeaderMenuMobile'
import MobileMenuButton from './MobileMenuButton'
import faMapMarkerAlt from '@fortawesome/fontawesome-free-solid/faMapMarkerAlt'
import {EventBus} from "../../utils/EventBus";
import svgIcon from "../includes/SVGIcon";

export default {
  name: "ThemeHeader",
  components: {
    HighlightButton,
    HeaderLogo,
    HeaderMenu,
    HeaderMenuMobile,
    MobileMenuButton,
    FilterProperty,
    svgIcon
  },
  data() {
    return {
      razzEmbedShouldOpen: window.razzEmbedShouldOpen,
      isMobile: false,
      isMobileMenuOpen: false,
      applyLink: false,
      isMounted: false,
    }
  },
  created() {

    this._onResize = _throttle(this.onResize, 300);
    window.addEventListener('resize', this._onResize);
    this._onResize();
  },
  mounted() {
    this.isMounted = true;
  },
  asyncComputed: {
    async wpMenu() {
      return razzMenuApi.get('/header-menu').then(res => res.data);
    }
  },
  computed: {
    mapMarker() {
      return faMapMarkerAlt;
    },
    wpSettings() {
      return wp.settings || false;
    },
    headerOptions() {
      return this.wpSettings && this.wpSettings.header_options;
    },
    smallLogo() {
      return this.wpSettings && this.wpSettings.small_logo || false;
    },
    searchLogo() {
      return this.wpSettings && this.wpSettings.logo || false;
    },
    highlightButtons() {
      return this.headerOptions && this.headerOptions.button_list;
    },
    leaseAddress() {
      return this.headerOptions && this.headerOptions.lease_address;
    },
    mapLinkLeaseAddress() {
      return this.headerOptions && this.headerOptions.map_link_lease;
    },
    headerClasses() {
      return `header ${this.pageScrolled || this.hideHeader ? 'scrolled' : ''} ${this.isMobile ? 'mobile-header' : ''}`
    },
    applyNowLink() {
      return this.headerOptions && this.headerOptions.apply_now_link
    },
    hideHeader() {
      return this.$route.path === '/floorplans/' || this.$route.path === '/floorplans';
    },
    filters() {
      return this.wpSettings && this.wpSettings.filter_options;
    },
    pageScrolled() {
      return EventBus.position > 5;
    },
    pageScrolledUp() {
      return EventBus.pageScrolledUp;
    },
    position() {
      return EventBus.position;
    },
  },
  methods: {
    onResize() {
      this.isMobile = window.innerWidth <= 1100
      if (this.isMobile && this.isMobileMenuOpen) document.body.classList.add('no-scroll');
      else document.body.classList.remove('no-scroll');
    },
    toggleMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
      document.body.classList.toggle('no-scroll')
    },
    closeMenu() {
      this.isMobileMenuOpen = false;
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(val, oldVal) {
        this.isMobileMenuOpen = false;
        document.body.classList.remove('no-scroll')
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.header {
  z-index: 999;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: transparent;
  transition: all 300ms ease-out;

  //XXX - I think this block is broken, specifically `scrolled-up` is missing the preceding class `.`
  &:not(scrolled-up) {
    pointer-events: none;

    .header-container {
      pointer-events: all;
    }

    .header-search {
      pointer-events: all;
    }
  }

  .logo {
    width: 25px;

    .svg-logo /deep/ {
      svg polygon {
        transition: all .3s ease-in-out;
      }
    }

    &.menu-open {
      .svg-logo /deep/ {
        svg polygon {
          fill: $color1;
        }
      }
    }
  }

  &.scrolled {

    .wrap-container {
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
    }

    .header-search {
      opacity: 0;
      margin-top: -50px;
      pointer-events: none;

    }

    &.scrolled-up, &.menu-open {
      .wrap-container {
        box-shadow: none;
      }

      .header-search {
        opacity: 1;
        margin-top: 0;
        pointer-events: all;
      }
    }
  }

  &.scrolled:not(.mobile-header) {

  }

  &.mobile-header {

    .header-search {

      .left {
        max-width: 355px;;
        padding: 10px 0;
        overflow: hidden;

        /deep/ img {
          margin-left: -60px;
        }
      }
    }
  }

}

.header-container {
  width: 100%;
  display: flex;
  align-items: center;
  background-color: #fff;
  @include max-width();
  margin: auto;
  position: relative;
  z-index: 10;

  .wrap-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
    z-index: 5;
  }

  .wrap-left {
    padding: 9px 35px;
    display: flex;
    align-items: center;

    @media (max-width: 1100px) {
      padding-left: 25px;
    }

    .logo {
      margin-right: 25px;
    }
  }

  .wrap-right {
    display: flex;
    align-items: center;

    .lease-address {
      display: flex;
      align-items: center;
      padding-right: 30px;

      @media (max-width: 1340px) {
        padding-right: 0;
      }

      /deep/ svg {
        font-size: 25px;
      }

      .map-lease {
        font-size: 14px;
        line-height: 24px;
        font-weight: 400;
        display: block;
        padding: 5px 20px 5px 15px;
        text-decoration: none;
        color: $color1;
        transition: all .3s ease-in-out;

        @media (max-width: 1310px) {
          padding-right: 10px;
        }

        &.map-link:hover {
          color: $color2;
        }
      }
    }

    .wrap-permalinks {
      display: flex;

      /deep/ .button {
        position: relative;

        &::before {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(#000, .4);
          opacity: 0;
          transition: all .3s ease-in-out;
        }

        &:first-child {
          position: relative;

          &::after {
            content: "";
            height: 70%;
            width: 1px;
            background-color: #fff;
            right: 0;
            left: auto;
            top: 50%;
            transform: translateY(-50%);
            position: absolute;
            opacity: 0;
          }
        }
      }
    }
  }
}

.header-search {
  background-color: #F9F9F9;
  box-shadow: 0 47px 39px -22px rgba(#000, .15) px;
  margin: 0 auto;
  transition: all .3s ease-in-out;
  @include max-width();


  .wrap-search {
    @include box-shadow;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (max-width: 767px) {
      flex-direction: column;
    }
  }

  .left {
    max-width: 505px;
    width: 100%;
    margin-left: 57px;
    padding: 60px 0 40px;

    /deep/ img {
      width: 100%;
      height: auto !important;

      @media (max-width: 767px) {
        margin-top: -10px;
      }

      @media (max-width: 380px) {
        margin-left: -39px !important;
        width: 90%;
      }
    }

    @media (max-width: 1100px) {
      width: 355px;
    }

    @media (max-width: 992px) {
      margin-left: 27px;
      width: 345px;
    }

    @media (max-width: 767px) {
      width: 100%;
      height: 45px;
      margin-left: 60px;
    }
  }

  .right {
    margin-right: 50px;
    margin-top: 35px;
    //display: none; //TODO Hidden until we fixed the IFPB issue

    h4 {
      text-transform: uppercase;
      letter-spacing: 4px;
      margin: 0 0 5px 22px;

      @media (max-width: 1100px) {
        font-size: 15px;
      }

      @media (max-width: 767px) {
        text-align: center;
        margin: 0 0 5px;
        font-size: 18px;
      }

      @media (max-width: 380px) {
        font-size: 15px;
      }
    }

    @media (max-width: 1100px) {
      margin: 15px 50px 15px 0;
    }

    @media (max-width: 992px) {
      margin: 15px 10px 15px 0;
    }

    @media (max-width: 767px) {
      width: 100%;

      margin: 15px 0;

      /deep/ .filtering-bar {
        max-width: 330px;
        margin: 0 auto;

        @media (max-width: 380px) {
          width: calc(100% - 30px);
        }
      }
    }
  }
}

</style>
